import { toast } from "sonner";
import { Button } from "@/components/catalyst/button";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { translateBlogPostMutation } from "@/app/Blogs/GraphQL/blogPostMutations";
import type { BlogPostRecord } from "@/app/Blogs/GraphQL/blogPostRecordQuery";
import { LanguagesIcon } from "lucide-react";
import { BlogTranslationLocaleRow } from "@/app/Blogs/Components/BlogTranslationLocaleRow";

const SUPPORTED_LOCALES = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "pt", label: "Portuguese" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "ja", label: "Japanese" },
  { code: "zh", label: "Chinese" },
] as const;

type Props = {
  post: BlogPostRecord;
};

export function BlogTranslationsPanel({ post }: Props) {
  const { mutateAsync: translate, isPending } = useGqlMutation(
    translateBlogPostMutation,
  );

  const translationsByLocale = new Map(
    post.translations.map((t) => [t.locale, t]),
  );
  const missingCount = SUPPORTED_LOCALES.filter(
    (l) => l.code !== post.locale && !translationsByLocale.has(l.code),
  ).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-600">
          {missingCount > 0
            ? `${missingCount} locale${missingCount === 1 ? "" : "s"} missing a translation.`
            : "All marketplace locales are translated."}
        </p>
        <Button
          onClick={async () => {
            await translate({ id: post.id, locale: null });
            toast.success(
              "Translation started — locales will appear once processed",
            );
          }}
          disabled={isPending || missingCount === 0}
          isLoading={isPending}
        >
          <LanguagesIcon data-slot="icon" />
          Translate missing
        </Button>
      </div>

      <ul className="divide-y divide-zinc-200 rounded-md border border-zinc-200 bg-white">
        {SUPPORTED_LOCALES.map((locale) => (
          <BlogTranslationLocaleRow
            key={locale.code}
            post={post}
            locale={locale}
            translation={translationsByLocale.get(locale.code)}
            isSource={locale.code === post.locale}
          />
        ))}
      </ul>
    </div>
  );
}
